import Modal from "@mui/material/Modal";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Stack,
  Link,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import PropTypes from "prop-types";

interface SyncErrorData {
  detail?: string;
  error_type?: string;
  reauth_url?: string | null;
  youtube_studio_url?: string;
}

interface SyncResultData {
  comments_fetched?: number;
  comments_saved?: number;
}

interface SyncCommentsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
  onSynced?: () => void;
}

type syncingStatusType = "initial" | "syncing" | "synced" | "failed";

export const SyncCommentsModal: React.FC<SyncCommentsModalProps> = ({
  open,
  setOpen,
  id,
  onSynced,
}) => {
  const [syncingStatus, setSyncingStatus] =
    useState<syncingStatusType>("initial");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorData, setErrorData] = useState<SyncErrorData | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResultData | null>(null);

  const syncComments = useCallback(async () => {
    setSyncingStatus("syncing");

    try {
      const response = await apiService.get<SyncResultData>(
        `/api/streams/stream/${id}/fetch-youtube-comments/`,
      );
      setSyncResult(response.data || null);
      setSyncingStatus("synced");
      onSynced?.();
    } catch (error) {
      const responseData = (
        error as { response?: { data?: SyncErrorData } }
      )?.response?.data;

      const errMsg =
        typeof responseData?.detail === "string"
          ? responseData.detail
          : "Failed to sync comments";

      setErrorMessage(errMsg);
      setErrorData(responseData || null);
      setSyncingStatus("failed");
    }
  }, [id, onSynced]);

  useEffect(() => {
    if (!open) {
      return;
    }
    syncComments();
  }, [open, syncComments]);

  const renderErrorContent = () => {
    if (!errorData?.error_type) {
      return (
        <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
          {errorMessage}
        </Typography>
      );
    }

    if (errorData.error_type === "insufficient_scopes") {
      return (
        <Box sx={{ textAlign: "center", mt: 2, px: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The YouTube channel does not have the required permissions to read
            comments. Please re-authenticate the channel to grant the necessary
            scopes.
          </Typography>
          {errorData.reauth_url && (
            <Link
              href={errorData.reauth_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              Re-authenticate YouTube Channel
            </Link>
          )}
        </Box>
      );
    }

    if (errorData.error_type === "comments_disabled") {
      return (
        <Box sx={{ textAlign: "center", mt: 2, px: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Comments are disabled for this YouTube video. Please enable comments
            on YouTube Studio and try again.
          </Typography>
          {errorData.youtube_studio_url && (
            <Link
              href={errorData.youtube_studio_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              Open YouTube Studio
            </Link>
          )}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
        {errorMessage}
      </Typography>
    );
  };

  return (
    <Modal
      open={open}
      //onClose={() => setOpen(false)}
      aria-labelledby="syncing-comments-modal"
      aria-describedby="syncing-comments-modal-description"
    >
      <Box
        sx={{
          borderRadius: 1,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            padding: "0 .5em",
            borderBottom: `1px solid whitesmoke`,
          }}
        >
          <IconButton sx={{ "&:hover": { color: "#a60b0bff" } }}>
            <CloseIcon onClick={() => setOpen(false)} />
          </IconButton>
        </Box>

        {syncingStatus === "syncing" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <CircularProgress size={30} />
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
              Syncing comments
            </Typography>
          </Stack>
        )}

        {syncingStatus === "synced" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <TaskAltIcon sx={{ width: 50, height: 50, color: "#048b3dff" }} />
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
              {syncResult?.comments_saved ?? 0} new comment
              {(syncResult?.comments_saved ?? 0) === 1 ? "" : "s"} synced
              {typeof syncResult?.comments_fetched === "number" && (
                <> ({syncResult.comments_fetched} total fetched)</>
              )}
            </Typography>
          </Stack>
        )}

        {syncingStatus === "failed" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <ErrorOutlineIcon
                sx={{ width: 50, height: 50, color: "#a60b0bff" }}
              />
            </Box>
            {renderErrorContent()}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

SyncCommentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
